/**
 * A candidate's interview, fetched outside the jobs state machine.
 *
 * This used to be a state of `jobsFSM`, and it never ran. The candidate page
 * fires its fetches back to back in one commit (candidate, interview, offer
 * events), the machine only accepts those events while it is idle, and the
 * first one moves it into a busy state with no handlers. XState drops an
 * event that no transition handles, silently, so the interview request was
 * discarded every time the candidate request was still in flight, which is
 * always. `candidateInterview` stayed null for every candidate, including ones
 * whose interview was complete, and nothing anywhere said so.
 *
 * Owning the request here fixes that, and gives the page the one signal the
 * machine could not: whether the answer has arrived. `null` with a status of
 * `loaded` means there is no interview. `null` with `loading` means we do not
 * know yet, and the page must not tell the recruiter "no interview" in that
 * state.
 *
 * A 404 is the backend's normal answer for "nothing for this candidate" and is
 * `loaded`, not an error. Anything else is `error`, kept local to this hook:
 * in the machine it sent the whole jobs app to its global ERROR state.
 */

import { useCallback, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import axios from '../../../axiosConfig';
import type { CandidateInterview } from '../../../fsm/jobs';

export type CandidateInterviewStatus = 'idle' | 'loading' | 'loaded' | 'error';

export const useCandidateInterview = () => {
  const [interview, setInterview] = useState<CandidateInterview | null>(null);
  const [status, setStatus] = useState<CandidateInterviewStatus>('idle');
  // Each load takes a ticket. A reply that is no longer the latest ticket is
  // for a candidate the recruiter has already navigated away from.
  const latestRequest = useRef(0);

  const load = useCallback(async (jobId: number, candidateId: number) => {
    const request = ++latestRequest.current;
    setInterview(null);
    setStatus('loading');
    try {
      const response = await axios.get<CandidateInterview>(
        `/api/jobs/${jobId}/candidates/${candidateId}/interview`,
      );
      if (request !== latestRequest.current) return;
      setInterview(response.data);
      setStatus('loaded');
    } catch (error) {
      if (request !== latestRequest.current) return;
      setInterview(null);
      setStatus(
        isAxiosError(error) && error.response?.status === 404
          ? 'loaded'
          : 'error',
      );
    }
  }, []);

  return { interview, status, load };
};
